import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { gql, useQuery } from '@apollo/client';
import { Font, StyleSheet, Text, View, PDFDownloadLink } from '@react-pdf/renderer';
import XPDF from 'src/components/pdf/XPDF';
import PDFTable from 'src/pages/cart/components/pdf/PDFTable';
import { Content } from 'src/components/light/Content';
import { SectionBlock } from 'src/components/light/SectionBlock';
import { Loading } from 'src/components/Loading';
import moment from 'moment';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const source = require('src/webfonts/khmer-regular.ttf');
Font.register({
  family: 'Khmer',
  src: source,
});

const styles = StyleSheet.create({
  infoContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 20,
    marginBottom: 10,
    fontFamily: 'Khmer',
    fontSize: '10pt',
  },
});

const QUERY_INVOICE_DETAIL = gql`
  query inventorySaleInvoiceDetail($invoiceNo: String!) {
    inventorySaleInvoiceDetail(invoiceNo: $invoiceNo)
  }
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function InvoiceDocument({ invoice }: { invoice: any }) {
  if (!invoice) return null;

  return (
    <XPDF>
      <View style={{ padding: 20, fontFamily: 'Khmer' }}>
        <XPDF.InvoiceHeader />

        {/* Info */}
        <View style={styles.infoContainer}>
          <View>
            <Text style={{ fontWeight: 'bold', fontSize: '12pt', marginBottom: 5, fontFamily: 'Khmer' }}>អតិថិជន</Text>
            <Text style={{ fontFamily: 'Khmer' }}>{invoice.customer_name || 'Customer'}</Text>
            <Text style={{ fontFamily: 'Khmer' }}>+855 {invoice.customer_phone || ''}</Text>
          </View>
          <View style={{ alignItems: 'flex-end' }}>
            <Text>{moment.utc(invoice.invoice_date).format('DD/MM/YYYY')}</Text>
            <Text>Invoice: {invoice.invoice_no}</Text>
          </View>
        </View>

        {/* Table */}
        <View style={{ marginTop: 10 }}>
          <PDFTable border>
            <PDFTable.thead>
              <PDFTable.row>
                <PDFTable.th text="Description" align="left" width={360} />
                <PDFTable.th text="Qty." width={40} align="right" />
                <PDFTable.th text="Unit Price" width={75} align="right" />
                <PDFTable.th text="Amount" width={75} align="right" />
              </PDFTable.row>
            </PDFTable.thead>
            <PDFTable.tbody>
              {invoice.items &&
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                invoice.items.map((item: any, idx: number) => (
                  <PDFTable.row key={idx}>
                    <PDFTable.td text={item.description || ''} align="left" width={360} />
                    <PDFTable.td text={item.qty?.toString()} width={40} align="right" />
                    <PDFTable.td
                      text={`$${Number(item.unit_price).toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
                      width={75}
                      align="right"
                    />
                    <PDFTable.td
                      text={`$${Number(item.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
                      width={75}
                      align="right"
                    />
                  </PDFTable.row>
                ))}

              {/* Totals */}
              <PDFTable.row>
                <PDFTable.td text="Total (USD)" align="left" width={360} />
                <PDFTable.td text="" width={40} />
                <PDFTable.td text="" width={75} />
                <PDFTable.td
                  text={`$${Number(invoice.total_usd).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`}
                  width={75}
                  align="right"
                />
              </PDFTable.row>
              <PDFTable.row>
                <PDFTable.td text="VAT Included (10%)" align="left" width={360} />
                <PDFTable.td text="" width={40} />
                <PDFTable.td text="" width={75} />
                <PDFTable.td
                  text={`$${Number(invoice.vat_included).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`}
                  width={75}
                  align="right"
                />
              </PDFTable.row>
            </PDFTable.tbody>
          </PDFTable>
        </View>

        <View style={{ marginTop: 20, paddingLeft: 5 }}>
          <Text style={{ fontSize: '10pt', fontFamily: 'Khmer' }}>Shop ID: {invoice.shop_id}</Text>
        </View>
      </View>
    </XPDF>
  );
}

export function InventorySaleInvoiceDetailScreen() {
  const { invoiceNo } = useParams<{ invoiceNo: string }>();
  const [isFontLoaded, setIsFontLoaded] = React.useState(false);

  React.useEffect(() => {
    const timer = setTimeout(() => setIsFontLoaded(true), 500);
    return () => clearTimeout(timer);
  }, []);

  const { loading, data } = useQuery(QUERY_INVOICE_DETAIL, {
    variables: { invoiceNo },
    fetchPolicy: 'network-only',
  });

  return (
    <Content>
      <SectionBlock title={`Invoice Preview: ${invoiceNo}`}>
        <div className="mb-3">
          <Link className="btn btn-secondary btn-sm" to="/invoices/inventory-sale">
            &larr; Back to List
          </Link>
        </div>
        {loading ? (
          <Loading />
        ) : !data || !data.inventorySaleInvoiceDetail ? (
          <div className="alert alert-warning">Invoice not found or no items in invoice.</div>
        ) : (
          <div>
            <div className="mb-3 text-right">
              <PDFDownloadLink
                className="btn btn-primary"
                document={<InvoiceDocument invoice={data.inventorySaleInvoiceDetail} />}
                fileName={`Invoice_${invoiceNo}.pdf`}
              >
                {({ loading }) => (loading ? 'Preparing PDF...' : 'Download PDF')}
              </PDFDownloadLink>
            </div>

            <div style={{ height: '70vh', width: '100%', border: '1px solid #ccc' }}>
              {isFontLoaded ? (
                <XPDF.Preview>
                  <InvoiceDocument invoice={data.inventorySaleInvoiceDetail} />
                </XPDF.Preview>
              ) : (
                <div style={{ padding: 20, textAlign: 'center' }}>Loading preview...</div>
              )}
            </div>
          </div>
        )}
      </SectionBlock>
    </Content>
  );
}
